/**
 * The role brief every InMail for this posting is drafted from, plus the per-role settings:
 * language, follow-up days and the open-web toggle. The criteria are editable; an edited brief is
 * never overwritten by "Rebuild" unless the recruiter rebuilds on purpose.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { BriefDoc, RoleBrief, WhereSetting } from '../../types/taOutreach';
import { BriefCriteria, SourcingKit } from './BriefCriteria';
import { AgentLabel, Badge } from './outreachUi';
import { ownerFirstName } from './ownerName';
import { InMailSettings } from './InMailSettings';
import { WhereEditor } from './WhereEditor';

/**
 * What the brief is, on the page itself (Sofia, Oct 15 2026: recruiters did not know). Open until
 * there is a brief, then collapsed to one line — the explanation matters before the first build,
 * not every visit after it.
 */
function BriefExplainer({ gmailConnected, hasBrief }: { gmailConnected: boolean; hasBrief: boolean }) {
  return (
    <details open={!hasBrief} className="max-w-3xl rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-4 py-3 text-[13px] text-[#1A1A1A]">
      <summary className="cursor-pointer font-semibold">What is the brief?</summary>
      <div className="mt-2 space-y-2">
        <p>
          The brief is everything about this role that a profile can show, written for you from the Lever posting and the whole kick-off
          sheet, scorecard included{gmailConnected ? '' : ' (the kick-off sheet only once Google is connected)'}. Pay and levels in the sheet
          are never read into it. Every other step reads from it:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <b>Who to look for</b> (top half) — the must-haves and nice-to-haves. A must-have is only what the scorecard’s Qualifying
            column asks for (without a scorecard: what the posting requires); everything else is a nice-to-have. Alternatives form a group
            — “at least 1 of these” — and the search counts the group, not each one. Each one says where it came from.
          </li>
          <li>
            <b>What to write</b> (bottom half) — the pitch points, location and work model. Every InMail draft for this role is written from
            these, so nothing in an InMail is made up.
          </li>
        </ul>
        <p>
          Read it once and fix what is wrong: remove a criterion, add one, move it between must-have and nice-to-have (⇅), change how many
          of a group are enough. It is the role’s brief: everyone on the role sees and edits this one. Edits are kept — “Rebuild” only
          replaces them when someone clicks it. Language and follow-up days below apply to every InMail for this role. Building the brief
          sends nothing and writes nothing to Lever.
        </p>
      </div>
    </details>
  );
}

const httpsUrl = (u: string | null | undefined) => (u && /^https:\/\//i.test(u) ? u : null);

/**
 * The kick-off sheet one click away; without one, the role's hiring-plan row in Airtable, so the
 * recruiter can look for it there (Sofia, Oct 19 2026).
 */
function SourceLinks({ brief }: { brief: BriefDoc | null }) {
  const kickoff = httpsUrl(brief?.source?.kickoffUrl);
  const airtable = httpsUrl(brief?.source?.airtableUrl);
  if (!brief) return null;
  return (
    <p className="m-0 text-[13px]">
      {kickoff ? (
        <a href={kickoff} target="_blank" rel="noreferrer" className="font-semibold text-[#0072EA] underline">
          Open the kick-off sheet
        </a>
      ) : (
        <span className="text-[#707070]">
          No kick-off sheet found.{' '}
          {airtable ? (
            <a href={airtable} target="_blank" rel="noreferrer" className="font-semibold text-[#0072EA] underline">
              Check the role in Airtable
            </a>
          ) : (
            'The role was not found in the hiring database.'
          )}
        </span>
      )}
    </p>
  );
}

export function OutreachBriefPanel({
  brief,
  compiling,
  gmailConnected,
  onConnectGmail,
  onCompile,
  onSave,
}: {
  brief: BriefDoc | null;
  compiling: boolean;
  gmailConnected: boolean;
  onConnectGmail: () => void;
  onCompile: (force: boolean) => void;
  onSave: (
    patch: Partial<{
      brief: Partial<RoleBrief>;
      language: 'de' | 'en';
      followUpAfterDays: number;
      inmailTemplate: { scope: 'company' | 'personal'; key: string } | null;
      enrichmentEnabled: boolean;
      where: WhereSetting | null;
    }>
  ) => void | Promise<void>;
}) {
  const b = brief?.brief;
  // Every criteria edit sends the whole list: a second edit while the first is saving would build on
  // the old list and overwrite it, so the criteria wait for the save (CodeRabbit, PR #662).
  const [savingCriteria, setSavingCriteria] = useState(false);
  const saveCriteria = async (patch: Partial<RoleBrief>) => {
    setSavingCriteria(true);
    try {
      await onSave({ brief: patch });
    } finally {
      setSavingCriteria(false);
    }
  };
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BriefExplainer gmailConnected={gmailConnected} hasBrief={Boolean(b)} />
        <div className="flex items-center gap-2">
          {!gmailConnected && (
            <Button variant="ghost" size="sm" onClick={onConnectGmail}>
              Connect Google to include the kick-off sheet
            </Button>
          )}
          <Button variant={b ? 'secondary' : 'primary'} size="sm" loading={compiling} disabled={compiling} onClick={() => onCompile(Boolean(b))}>
            {b ? 'Rebuild from posting and kick-off sheet' : 'Build brief'}
          </Button>
        </div>
      </div>

      <SourceLinks brief={brief} />
      {/* One brief per role, edited by everyone (Oct 27 2026): who changed it last. */}
      {brief?.updatedBy && <p className="m-0 text-xs text-[#707070]">Last edited by {ownerFirstName(brief.updatedBy)}</p>}

      {!b && <p className="text-[13px] text-[#707070]">No brief yet. Build it first: the profile check and every draft are written from it.</p>}

      {b && (
        <div className="rounded-md border border-solid border-[#E9EAEC] bg-white px-[22px] py-[18px]">
          <AgentLabel>For the sourcing agent — who to look for</AgentLabel>
          {b.summary && <p className="mb-3 text-[13px] text-[#1A1A1A]">{b.summary}</p>}
          <BriefCriteria brief={b} disabled={compiling || savingCriteria} onChange={(patch) => void saveCriteria(patch)} />
          <SourcingKit kit={b.sourcingKit} />
          <p className="mt-2 text-xs text-[#979797]">
            From {brief?.source?.kickoff ? 'the posting and the kick-off sheet' : 'the public posting only'}. Find people (step 2) searches and
            screens against these.
          </p>

          <div className="my-4 h-px bg-[#E9EAEC]" />
          <AgentLabel>For the outreach agent — what to write</AgentLabel>
          <div className="text-[13px]">
            <span className="block text-xs font-semibold text-[#707070]">Message blocks · the template below decides the wording</span>
            <ul className="mt-1 space-y-1">
              <li><Badge tone="blue">AI writes</Badge> A personal sentence on their profile{brief?.enrichmentEnabled ? ' and public web facts' : ''} — in every InMail</li>
              <li><Badge tone="blue">AI writes</Badge> The role in one or two sentences, from this brief</li>
              {brief?.whereView?.sentence[brief.language] ? (
                <li><Badge tone="grey">Filled in</Badge> Where the role is and how it works — the sentence below, word for word</li>
              ) : (
                <li><Badge tone="blue">AI writes</Badge> Where the role is and how it works, from this brief — set it below to fix the wording</li>
              )}
              <li><Badge tone="grey">Filled in</Badge> Name, role title, salary range, job ad and your booking link — as links</li>
              <li><Badge tone="grey">Template</Badge> Everything else, word for word. No question to the person: it ends with a call to action.</li>
            </ul>
          </div>
          {brief && <WhereEditor brief={brief} onSave={(where) => onSave({ where })} />}
          {b.pitchPoints.length > 0 && (
            <div className="mt-3">
              <span className="block text-xs font-semibold text-[#707070]">Why the role is attractive · from the posting</span>
              <ul className="mt-1 list-disc pl-5 text-[13px]">
                {b.pitchPoints.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {brief && (
        <div className="flex flex-wrap items-end gap-4 rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-[18px] py-[13px] text-[13px]">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[#707070]">Outreach language</span>
            <select className="h-8 rounded-md border border-solid border-[#E9EAEC] bg-white px-2" value={brief.language} onChange={(e) => onSave({ language: e.target.value as 'de' | 'en' })}>
              <option value="en">English</option>
              <option value="de">German (du)</option>
            </select>
          </label>
          <InMailSettings brief={brief} onSave={onSave} />
          <span className="w-full text-xs text-[#1A1A1A]">
            <b>Salary in every InMail:</b>{' '}
            {brief.salary ? brief.salary.text : <span className="text-[#B96716]">no salary range in the Lever posting — add one to the posting in Lever; it is picked up the next time this brief is opened</span>}
            {' · '}
            <b>Job ad:</b>{' '}
            {brief.jobUrl ? (
              <a href={brief.jobUrl} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
                {brief.jobUrl}
              </a>
            ) : (
              <span className="text-[#B96716]">no public link on the Lever posting</span>
            )}
          </span>
          <span className="text-xs text-[#707070]">
            Sending: up to ~20 per day from your seat. LinkedIn allows about 150 InMails per seat per month and throttles above roughly 25 a day.
          </span>
          <label className="flex max-w-md items-start gap-2">
            <input type="checkbox" className="mt-1" checked={brief.enrichmentEnabled} onChange={(e) => onSave({ enrichmentEnabled: e.target.checked })} />
            <span>
              Search the public web for professional facts (talks, open source, articles). Never LinkedIn or people-search sites.
              <span className="block text-xs text-[#707070]">
                Data about people who did not apply (GDPR Art. 14): only facts used in a sent InMail are kept, 90 days; the rest is deleted after 30 days.
              </span>
            </span>
          </label>
        </div>
      )}
    </section>
  );
}
